/**
 * Utility functions for VAT number handling in French accounting
 */

/**
 * Generates French intracom VAT number from a French VAT number (SIRET)
 *
 * @param vatNumber - The French VAT number (SIRET) - can contain spaces
 * @returns The generated intracom VAT number in format "FR{key}{siren}" or empty string if invalid
 *
 * @example
 * generateIntracomVatNumber("123456789") // Returns "FR32123456789"
 * generateIntracomVatNumber("123 456 789") // Returns "FR32123456789"
 * generateIntracomVatNumber("12345") // Returns "" (too short)
 */
export function generateIntracomVatNumber(vatNumber: string): string {
  if (!vatNumber) {
    return "";
  }

  // Remove all spaces from the VAT number
  const cleanVatNumber = vatNumber.replace(/\s/g, "");

  // Extract the first 9 digits (SIREN part)
  const siren = Number(cleanVatNumber.substring(0, 9));

  // Check if we have at least 9 digits and it's a valid number
  if (cleanVatNumber.length < 9 || !siren || isNaN(siren)) {
    return "";
  }

  // Calculate the intracom key using French algorithm
  // Formula: (12 + 3 * (siren % 97)) % 97
  const intracomKey = (12 + 3 * (siren % 97)) % 97;

  // Format the result: FR + key (padded to 2 digits) + siren
  const paddedKey = String(intracomKey).padStart(2, "0");

  return `FR${paddedKey}${siren}`;
}

/**
 * Validates if a French intracom VAT number is correctly formatted
 *
 * @param intracomVatNumber - The intracom VAT number to validate
 * @returns true if the format is valid, false otherwise
 *
 * @example
 * validateIntracomVatNumber("FR32123456789") // Returns true
 * validateIntracomVatNumber("FR123456789") // Returns false (invalid key)
 * validateIntracomVatNumber("DE123456789") // Returns false (not French)
 */
export function validateIntracomVatNumber(intracomVatNumber: string): boolean {
  if (!intracomVatNumber || typeof intracomVatNumber !== "string") {
    return false;
  }

  // Check if it starts with FR and has the correct length (13 characters total)
  const frenchVatRegex = /^FR\d{11}$/;
  if (!frenchVatRegex.test(intracomVatNumber)) {
    return false;
  }

  // Extract the key and siren
  const key = parseInt(intracomVatNumber.substring(2, 4), 10);
  const siren = parseInt(intracomVatNumber.substring(4, 13), 10);

  // Recalculate the expected key
  const expectedKey = (12 + 3 * (siren % 97)) % 97;

  return key === expectedKey;
}

/**
 * Extracts the SIREN (9-digit company identifier) from a French intracom VAT number
 *
 * @param intracomVatNumber - The intracom VAT number
 * @returns The SIREN as a string, or empty string if invalid
 *
 * @example
 * extractSirenFromIntracom("FR32123456789") // Returns "123456789"
 */
export function extractSirenFromIntracom(intracomVatNumber: string): string {
  if (!validateIntracomVatNumber(intracomVatNumber)) {
    return "";
  }

  return intracomVatNumber.substring(4, 13);
}
